import { Folder } from "lucide-react";

import { useDSDFiles } from "@/hooks/use-dsd-files";
import { DsdFileStatus, type DsdFile } from "@/types/dsd";
import { ExistingReportPicker } from "@/components/common/existing-report-picker";
import {
  PeriodChip,
  TypeChip,
  YearChip,
  type ReportPeriod,
} from "@/components/common/report-chip";

interface WorkbookPickerProps {
  selectedWorkbookId: string | null;
  onSelect: (workbookId: string | null, workbook: DsdFile | null) => void;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  className?: string;
  listHeight?: "cap" | "fill";
  selectedTone?: "mono" | "blue";
}

const isReportPeriod = (value: string | null): value is ReportPeriod =>
  /^Q[1-4]$/.test(value ?? "");

const WorkbookChips = ({
  financialStatementType,
  reportYear,
  reportQuarter,
}: {
  financialStatementType: string;
  reportYear: number | null;
  reportQuarter: string | null;
}) => (
  <>
    <TypeChip
      type={
        financialStatementType === "CONSOLIDATED" ? "CONSOLIDATED" : "SEPARATE"
      }
      className="shrink-0"
    />
    {reportYear != null && <YearChip year={reportYear} className="shrink-0" />}
    {isReportPeriod(reportQuarter) && (
      <PeriodChip period={reportQuarter} className="shrink-0" />
    )}
  </>
);

export const WorkbookPicker = ({
  selectedWorkbookId,
  onSelect,
  title = "Workbook 선택",
  description = "비교할 workbook을 선택합니다",
  searchPlaceholder,
  className,
  listHeight,
  selectedTone,
}: WorkbookPickerProps) => {
  // DSD 워크북 관리 페이지(report-list)와 같은 훅·필터를 써서
  // 목록 구성과 (revision) 라벨이 관리 페이지와 항상 동일하게 유지되도록 한다
  const { data, isLoading, isError, refetch } = useDSDFiles({
    status: DsdFileStatus.DSD_ANALYZED,
  });
  const workbooks = data ?? [];

  return (
    <ExistingReportPicker
      title={title}
      description={description}
      reports={workbooks}
      selectedReportId={selectedWorkbookId}
      onSelect={onSelect}
      getReportId={(workbook) => workbook.id}
      getSearchText={(workbook) => `${workbook.label} ${workbook.name}`}
      searchPlaceholder={searchPlaceholder}
      showSelectionIndicator
      renderReportTitle={(workbook) => (
        <span className="flex min-w-0 items-center gap-1">
          <WorkbookChips
            financialStatementType={workbook.financialStatementType}
            reportYear={workbook.reportYear}
            reportQuarter={workbook.reportQuarter}
          />
          <span className="truncate">{workbook.label}</span>
        </span>
      )}
      renderSelectedReport={(workbook) => (
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-[8px] bg-blue-100 text-blue-500">
            <Folder className="size-6 fill-current" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex min-w-0 items-center gap-1">
              <WorkbookChips
                financialStatementType={workbook.financialStatementType}
                reportYear={workbook.reportYear}
                reportQuarter={workbook.reportQuarter}
              />
            </span>
            <span className="truncate body-2-normal-medium text-label-normal">
              {workbook.label}
            </span>
          </span>
        </div>
      )}
      isLoading={isLoading}
      isError={isError}
      emptyMessage="workbook이 없습니다."
      errorMessage="workbook 목록을 불러오는 중 오류가 발생했습니다."
      onRetry={() => refetch()}
      className={className}
      listHeight={listHeight}
      selectedTone={selectedTone}
    />
  );
};
